import { supabase } from '@/lib/supabase/client';

export interface NewsletterSubscriber {
  id: string;
  email: string;
  subscribed_at: string;
  is_active: boolean;
}

export interface NewsletterSubscribeResponse {
  success: boolean;
  message: string;
  data?: NewsletterSubscriber;
}

/**
 * Subscribe a new email to the newsletter
 */
export async function subscribeToNewsletter(email: string): Promise<NewsletterSubscribeResponse> {
  try {
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return {
        success: false,
        message: 'Por favor, insira um e-mail válido.'
      };
    }

    // Check if email already exists
    const { data: existing } = await supabase
      .from('newsletter')
      .select('email')
      .eq('email', email)
      .single();

    if (existing) {
      return {
        success: false,
        message: 'Este e-mail já está inscrito em nossa newsletter.'
      };
    }

    // Insert new subscriber
    const { data, error } = await supabase
      .from('newsletter')
      .insert([{ email }])
      .select()
      .single();

    if (error) {
      console.error('Error subscribing to newsletter:', error);
      return {
        success: false,
        message: 'Erro ao processar a inscrição. Por favor, tente novamente.'
      };
    }

    return {
      success: true,
      message: 'Obrigado por se inscrever! Você receberá nossas novidades em breve.',
      data
    };
  } catch (error) {
    console.error('Error in subscribeToNewsletter:', error);
    return {
      success: false,
      message: 'Erro ao processar a inscrição. Por favor, tente novamente.'
    };
  }
}

/**
 * Get all newsletter subscribers (for admin panel)
 */
export async function getAllSubscribers(): Promise<NewsletterSubscriber[]> {
  try {
    const { data, error } = await supabase
      .from('newsletter')
      .select('*')
      .order('subscribed_at', { ascending: false });

    if (error) {
      console.error('Error fetching subscribers:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getAllSubscribers:', error);
    return [];
  }
}

/**
 * Delete a subscriber by ID (for admin panel)
 */
export async function deleteSubscriber(id: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('newsletter')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting subscriber:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteSubscriber:', error);
    return false;
  }
}

/**
 * Get subscriber count
 */
export async function getSubscriberCount(): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('newsletter')
      .select('*', { count: 'exact', head: true })
      .eq('is_active', true);

    if (error) {
      console.error('Error getting subscriber count:', error);
      return 0;
    }

    return count || 0;
  } catch (error) {
    console.error('Error in getSubscriberCount:', error);
    return 0;
  }
}